import { useEffect, useMemo, useRef } from "react";
import ForceGraph2D, {
  type ForceGraphMethods,
} from "react-force-graph-2d";
import type { SearchEdge, SearchNode } from "../api/types";
import { depthColor, truncate } from "../lib/format";

// Use loose typings — react-force-graph-2d's generic types are over-wrapped
// (they apply NodeObject<> twice through GraphData), so we pass the library
// plain objects with our payload attached and read it through accessors.
export type GraphNode = {
  id: string;
  data: SearchNode;
  x?: number;
  y?: number;
};

export type GraphLink = {
  source: string | GraphNode;
  target: string | GraphNode;
  data: SearchEdge;
};

const linkEnd = (e: string | GraphNode): string =>
  typeof e === "object" ? e.id : e;

const labelVisible = (node: SearchNode, selected: boolean, hovered: boolean, scale: number) =>
  node.depth === 0 || selected || hovered || scale > 1.6;

type Props = {
  nodes: SearchNode[];
  edges: SearchEdge[];
  selectedId: string | null;
  hoveredId: string | null;
  onSelect: (id: string | null) => void;
  onHover: (id: string | null) => void;
  width: number;
  height: number;
};

export const Graph = ({
  nodes,
  edges,
  selectedId,
  hoveredId,
  onSelect,
  onHover,
  width,
  height,
}: Props) => {
  const fgRef = useRef<ForceGraphMethods | undefined>(undefined);

  const data = useMemo(() => {
    const ids = new Set(nodes.map((n) => n.id));
    const gNodes: GraphNode[] = nodes.map((n) => ({ id: n.id, data: n }));
    const gLinks: GraphLink[] = edges
      .filter((e) => ids.has(e.source_node_id) && ids.has(e.target_node_id))
      .map((e) => ({
        source: e.source_node_id,
        target: e.target_node_id,
        data: e,
      }));
    return { nodes: gNodes, links: gLinks };
  }, [nodes, edges]);

  // Map of node id -> incident link count, used for sizing.
  const degree = useMemo(() => {
    const d = new Map<string, number>();
    for (const e of edges) {
      d.set(e.source_node_id, (d.get(e.source_node_id) ?? 0) + 1);
      d.set(e.target_node_id, (d.get(e.target_node_id) ?? 0) + 1);
    }
    return d;
  }, [edges]);

  // Recenter when the dataset changes.
  useEffect(() => {
    const fg = fgRef.current;
    if (!fg) return;
    const t = setTimeout(() => fg.zoomToFit(600, 80), 250);
    return () => clearTimeout(t);
  }, [data]);

  // Tune forces for a roomier layout.
  useEffect(() => {
    const fg = fgRef.current;
    if (!fg) return;
    const charge = fg.d3Force("charge") as unknown as
      | { strength: (s: number) => unknown }
      | null;
    if (charge && typeof charge.strength === "function") charge.strength(-180);
    const link = fg.d3Force("link") as unknown as
      | { distance: (d: number) => unknown }
      | null;
    if (link && typeof link.distance === "function") link.distance(70);
  }, [data]);

  const baseRadius = (id: string) => 5 + Math.min(7, (degree.get(id) ?? 0));

  const labelLayout = (
    node: GraphNode,
    ctx: CanvasRenderingContext2D,
    scale: number,
  ) => {
    const safeScale = Math.max(scale, 0.001);
    const r = baseRadius(node.id) / safeScale;
    const fontSize = 12 / safeScale;
    ctx.font = `500 ${fontSize}px Inter, system-ui, sans-serif`;
    const label = truncate(node.data.title, 36);
    const tx = (node.x ?? 0) + r + 4;
    const ty = (node.y ?? 0) + fontSize / 3;
    const width = ctx.measureText(label).width;

    return {
      label,
      fontSize,
      textX: tx,
      textY: ty,
      rectX: tx - 2,
      rectY: ty - fontSize + 2,
      rectWidth: width + 4,
      rectHeight: fontSize + 2,
    };
  };

  return (
    <ForceGraph2D
      ref={fgRef}
      width={width}
      height={height}
      graphData={data as never}
      backgroundColor="rgba(0,0,0,0)"
      cooldownTicks={120}
      d3AlphaDecay={0.025}
      d3VelocityDecay={0.35}
      enableNodeDrag={true}
      onNodeClick={(n: { id?: string | number }) =>
        onSelect((n.id as string) ?? null)
      }
      onBackgroundClick={() => onSelect(null)}
      onNodeHover={(n: { id?: string | number } | null) => {
        onHover((n?.id as string | undefined) ?? null);
        const canvas = document.querySelector<HTMLCanvasElement>("canvas");
        if (canvas) canvas.style.cursor = n ? "pointer" : "default";
      }}
      linkColor={(l: unknown) => {
        const link = l as GraphLink;
        const sId = linkEnd(link.source);
        const tId = linkEnd(link.target);
        const isIncident =
          (selectedId && (sId === selectedId || tId === selectedId)) ||
          (hoveredId && (sId === hoveredId || tId === hoveredId));
        return isIncident
          ? "rgba(192, 132, 252, 0.7)"
          : "rgba(168, 85, 247, 0.18)";
      }}
      linkWidth={(l: unknown) => {
        const link = l as GraphLink;
        const sId = linkEnd(link.source);
        const tId = linkEnd(link.target);
        const isIncident =
          (selectedId && (sId === selectedId || tId === selectedId)) ||
          (hoveredId && (sId === hoveredId || tId === hoveredId));
        return isIncident ? 1.4 : 0.6;
      }}
      linkDirectionalParticles={(l: unknown) => {
        const link = l as GraphLink;
        const sId = linkEnd(link.source);
        const tId = linkEnd(link.target);
        return (selectedId && (sId === selectedId || tId === selectedId)) ||
          (hoveredId && (sId === hoveredId || tId === hoveredId))
          ? 2
          : 0;
      }}
      linkDirectionalParticleWidth={2}
      linkDirectionalParticleColor={() => "#C084FC"}
      nodeCanvasObjectMode={() => "replace"}
      nodeCanvasObject={(
        n: unknown,
        ctx: CanvasRenderingContext2D,
        scale: number,
      ) => {
        const node = n as GraphNode;
        if (node.x === undefined || node.y === undefined) return;
        const safeScale = Math.max(scale, 0.001);
        const r = baseRadius(node.id) / safeScale;
        const isSelected = selectedId === node.id;
        const isHovered = hoveredId === node.id;
        const color = depthColor(node.data.depth);

        if (isSelected || isHovered) {
          const grad = ctx.createRadialGradient(
            node.x,
            node.y,
            0,
            node.x,
            node.y,
            r * 4,
          );
          grad.addColorStop(0, `${color}66`);
          grad.addColorStop(1, `${color}00`);
          ctx.fillStyle = grad;
          ctx.beginPath();
          ctx.arc(node.x, node.y, r * 4, 0, 2 * Math.PI);
          ctx.fill();
        }

        ctx.beginPath();
        ctx.arc(node.x, node.y, r, 0, 2 * Math.PI);
        ctx.fillStyle = color;
        ctx.fill();
        if (isSelected) {
          ctx.lineWidth = 1.5 / scale;
          ctx.strokeStyle = "#ffffff";
          ctx.stroke();
        }

        if (labelVisible(node.data, isSelected, isHovered, scale)) {
          const label = labelLayout(node, ctx, scale);
          ctx.fillStyle = "rgba(9,8,13,0.7)";
          ctx.fillRect(
            label.rectX,
            label.rectY,
            label.rectWidth,
            label.rectHeight,
          );
          ctx.fillStyle = isSelected ? "#ffffff" : "rgba(212, 208, 224, 0.9)";
          ctx.fillText(label.label, label.textX, label.textY);
        }
      }}
      nodePointerAreaPaint={(
        n: unknown,
        color: string,
        ctx: CanvasRenderingContext2D,
        scale: number,
      ) => {
        const node = n as GraphNode;
        if (node.x === undefined || node.y === undefined) return;
        const safeScale = Math.max(scale, 0.001);
        const r = (baseRadius(node.id) + 8) / safeScale;
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(node.x, node.y, r, 0, 2 * Math.PI);
        ctx.fill();

        const isSelected = selectedId === node.id;
        const isHovered = hoveredId === node.id;
        if (labelVisible(node.data, isSelected, isHovered, scale)) {
          const label = labelLayout(node, ctx, scale);
          ctx.fillRect(
            label.rectX - 4 / safeScale,
            label.rectY - 3 / safeScale,
            label.rectWidth + 8 / safeScale,
            label.rectHeight + 6 / safeScale,
          );
        }
      }}
    />
  );
};
